/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { Users } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import AuthCard from '@/client/components/AuthCard';
import Page from '@/client/pages/_layout/_page';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const PLATFORM_BENEFITS = [
    {
        title: 'Scale-ready operations',
        description: 'Coordinate sourcing, interviewing, messaging, and reporting from a single command center.',
    },
    {
        title: 'Talent pods on demand',
        description: 'Spin up sourcing squads or fractional RPO teams to reinforce your internal capacity in days.',
    },
    {
        title: 'Compliance & payouts handled',
        description: 'CrossPath manages contracts, administration, and success-fee payouts so you focus on outcomes.',
    },
    {
        title: 'AI copilots & automations',
        description: 'Multichannel cadences, enrichment, and performance dashboards included out of the box.',
    },
];

const LOGO_URL = 'https://sitlmwrkxbvpqzfegvww.supabase.co/storage/v1/object/public/static/logoCP.png';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function PlatformInboundLanding() {
    const [mode, setMode] = React.useState<'login' | 'signup'>('signup');
    const isSignUp = mode === 'signup';

    return (
        <Page withLayout={false}>
            <div className="min-h-screen bg-slate-50 text-slate-900">
                <header className="bg-white border-b border-slate-100 shadow-sm sticky top-0 z-30">
                    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="CrossPath Logo" className="w-11 h-11 rounded-xl" />
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.45em] text-orange-500 font-semibold">
                                    CrossPath
                                </p>
                                <p className="text-base font-semibold text-slate-700">Recruiter Platform</p>
                            </div>
                        </div>
                        <a
                            href="/login"
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                        >
                            Already a member? Sign in
                        </a>
                    </div>
                </header>

                <main className="max-w-6xl mx-auto px-6 py-14 space-y-16">
                    <section className="text-center space-y-4">
                        <h1 className="text-4xl lg:text-5xl font-black leading-tight">
                            Access recruitment missions from companies worldwide.
                        </h1>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Browse open roles, submit candidates, and get paid when placements succeed.
                        </p>
                    </section>

                    <section className="flex flex-col items-center space-y-8">
                        <div className="text-center space-y-4 max-w-2xl">
                            <h2 className="text-3xl lg:text-4xl font-black leading-tight">
                                <span className="text-orange-500">Join </span>
                                <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-pink-600 bg-clip-text text-transparent">
                                    10,000+
                                </span>
                                <span className="text-orange-500"> Recruiters </span>
                                <span className="bg-gradient-to-r from-pink-500 via-pink-600 to-purple-500 bg-clip-text text-transparent">
                                    Earning
                                </span>
                                <span className="text-orange-500"> Worldwide</span>
                            </h2>
                            <p className="text-lg text-slate-800">
                                Work on recruitment missions from top companies. Choose your projects, set your pace,
                                and get paid for successful placements.
                            </p>
                        </div>

                        <div className="w-full max-w-md">
                            <div className="flex flex-col items-center space-y-4 mb-6">
                                <div className="flex items-center gap-3">
                                    <img src={LOGO_URL} alt="CrossPath Logo" className="w-12 h-12 rounded-xl" />
                                    <p className="text-xl font-bold text-orange-500">CrossPath</p>
                                </div>
                                <div className="text-center space-y-1">
                                    <h3 className="text-2xl font-bold text-slate-900">
                                        {isSignUp ? 'Join CrossPath' : 'Welcome back'}
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        {isSignUp
                                            ? 'Create your account to start using CrossPath.'
                                            : 'Log in to access your account.'}
                                    </p>
                                </div>
                            </div>

                            <AuthCard
                                mode={mode}
                                onModeChange={setMode}
                                showModeTabs={true}
                                showGoogleAuth={true}
                            />
                        </div>
                    </section>

                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {PLATFORM_BENEFITS.map((benefit) => (
                            <div
                                key={benefit.title}
                                className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm"
                            >
                                <div className="w-11 h-11 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center mb-4">
                                    <Users className="w-5 h-5" />
                                </div>
                                <h3 className="text-base font-bold mb-2">{benefit.title}</h3>
                                <p className="text-sm text-slate-600">{benefit.description}</p>
                            </div>
                        ))}
                    </section>
                </main>
            </div>
        </Page>
    );
}
